/**
 * Section Editor Component
 * Edit custom sections (supports both manual text and AI generation modes)
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Select } from '../UI';
import { Save, X, RefreshCw, Brain, Type, Loader2 } from 'lucide-react';
import { type ReportSection } from '../../api/reports';
// RichTextEditor will be used if available, otherwise fallback to textarea

interface SectionEditorProps {
  section: ReportSection;
  reportId: string;
  mode: 'create' | 'edit';
  onSave: (section: ReportSection, regenerate?: boolean) => Promise<void> | void;
  onCancel: () => void;
}

export function SectionEditor({
  section,
  reportId,
  mode,
  onSave,
  onCancel,
}: SectionEditorProps) {
  const [sectionName, setSectionName] = useState(section.section_name);
  const [question, setQuestion] = useState(section.question || '');
  const [answer, setAnswer] = useState(section.answer || '');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSectionName(section.section_name);
    setQuestion(section.question || '');
    setAnswer(section.answer || '');
  }, [section]);

  const handleSave = async () => {
    if (!sectionName.trim()) {
      alert('Section name is required');
      return;
    }
    if (isManual && !answer.trim()) {
      alert('Section content is required');
      return;
    }
    if (!isManual && !question.trim()) {
      alert('Question is required for AI sections');
      return;
    }
    setIsSaving(true);
    const updatedSection: ReportSection = {
      ...section,
      section_name: sectionName,
      question: question,
      answer: answer,
    };
    try {
      await onSave(updatedSection);
    } catch (error) {
      console.error('Failed to save section:', error);
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (section.content_mode !== 'ai') {
      alert('Only AI-generated sections can be regenerated');
      return;
    }

    if (!question && !section.question) {
      alert('Question is required for regeneration');
      return;
    }

    setIsRegenerating(true);
    try {
      // Trigger regeneration via parent component with regenerate flag
      const updatedSection: ReportSection = {
        ...section,
        question: question || section.question,
      };
      // Pass regenerate=true to parent
      onSave(updatedSection, true);
    } catch (error) {
      console.error('Failed to regenerate:', error);
      alert('Failed to regenerate section. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const isManual = section.content_mode === 'manual';

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={mode === 'edit' ? 'Edit Section' : 'Create Section'}
      size="lg"
    >
      <div className="space-y-4">
        {/* Section Name */}
        <Input
          label="Section Name"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          required
        />

        {/* Question (for AI sections) */}
        {!isManual && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Question for AI Generation
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What should this section cover?"
              rows={3}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Brain className="h-3 w-3" />
              <span>AI-generated content will be updated when you save</span>
            </div>
          </div>
        )}

        {/* Content Editor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {isManual ? 'Section Content' : 'Generated Content (Preview)'}
          </label>
          {isManual ? (
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter section content..."
              rows={10}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800 min-h-[200px]">
              <div className="prose dark:prose-invert max-w-none">
                {answer || 'Content will be generated when you save...'}
              </div>
            </div>
          )}
        </div>

        {/* Regenerate Button (for AI sections) */}
        {!isManual && (
          <Button
            variant="outline"
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="w-full"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
            {isRegenerating ? 'Regenerating...' : 'Regenerate Content'}
          </Button>
        )}

        {/* Section Info */}
        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t">
          <span>Type: {section.section_type || 'standard'}</span>
          <span>Mode: {section.content_mode === 'ai' ? 'AI Generated' : 'Manual'}</span>
          {section.confidence !== undefined && (
            <span>Confidence: {section.confidence}%</span>
          )}
          {section.citations && (
            <span>Citations: {section.citations.length}</span>
          )}
          {(() => {
            // Get word count from metadata or calculate from answer
            const wordCount = section.section_metadata?.word_count ?? 
              (section.answer ? section.answer.split(/\s+/).filter((w: string) => w.length > 0).length : 0);
            return wordCount > 0 ? <span>Words: {wordCount.toLocaleString()}</span> : null;
          })()}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isRegenerating}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
